import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { useIdleTimeContext } from "@labeling-tools/ui-components/src";
import { values } from "lodash";
import { useCallback, useEffect, useRef } from "react";

/** Active-time metrics recorded into the task result.metadata at submit. */
export interface CoverageBetaTiming {
  /** Wall-clock ms from task open to submit. */
  total_duration: number;
  /** total_duration minus all paused time (idle / away / hidden / sleep). */
  active_duration: number;
  /** Sum of paused time. */
  idle_duration: number;
  /** Active ms from task open until the first labeling action; null if none. */
  time_to_first_action: number | null;
  /** Wall-clock ms spent with each empty area active (keyed by feEmptyAreaIdx). */
  area_active_ms: Record<number, number>;
  /** How many times the active empty area changed (a hunting/back-and-forth proxy). */
  area_switches: number;
}

// Module-singleton bridge: the tracker (mounted inside IdleTimeProvider)
// registers a getter here and the submit button reads it. The submit button
// lives in the header — outside IdleTimeProvider — so a React context would not
// reach it without restructuring the page; this keeps the change contained.
let timingGetter: (() => CoverageBetaTiming) | null = null;
export const readCoverageBetaTiming = (): CoverageBetaTiming | null =>
  timingGetter ? timingGetter() : null;

/** Wall-clock gap on a 1 s tick above which the JS engine was suspended (sleep). */
const SLEEP_GAP_MS = 3_000;
/** How long the window must be blurred before it counts as away time. */
const WINDOW_BLUR_GRACE_MS = 5_000;

interface Interval {
  start: number;
  end: number;
}

/** Merge a sorted list of possibly-overlapping intervals into disjoint ones. */
function mergeIntervals(sorted: Interval[]): Interval[] {
  const result: Interval[] = [];
  for (const iv of sorted) {
    const last = result[result.length - 1];
    if (last && iv.start <= last.end) {
      last.end = Math.max(last.end, iv.end);
    } else {
      result.push({ ...iv });
    }
  }
  return result;
}

/** A labeling action has happened once any non-deleted area is categorized, a
 *  person box is drawn, or a planogram was replaced/added. */
function hasLabelingAction(
  state: ReturnType<typeof useCoverageBetaStore.getState>,
): boolean {
  return (
    values(state.idxToFeEmptyArea).some(
      (a) => !a.isDeleted && (a.selectedProducts.length > 0 || a.isValid),
    ) ||
    state.persons.some((p) => !p.isDeleted) ||
    state.extraPlanogramIds.length > 0 ||
    Object.keys(state.overridePlanogramIds).length > 0
  );
}

/**
 * Headless tracker for CoverageBeta. Measures active working time by subtracting
 * every pause source from wall-clock time — mirroring the confirmation flow's
 * TimeTrackingProvider, but reporting coverage-relevant metrics. Must be
 * rendered inside an IdleTimeProvider (reads isIdle from it).
 *
 * Pause sources: idle (IdleTimeContext), tab hidden (visibilitychange), computer
 * sleep (heartbeat gap), and alt-tab away (window blur/focus with a grace).
 */
export function CoverageBetaActivityTracker({ taskId }: { taskId: string }) {
  const { isIdle } = useIdleTimeContext();

  const taskStartRef = useRef(Date.now());
  const isPausedRef = useRef(false);
  const pauseStartRef = useRef<number | null>(null);
  const pauseIntervalsRef = useRef<Interval[]>([]);
  const lastTickRef = useRef(Date.now());
  const blurStartRef = useRef<number | null>(null);
  const firstActionActiveRef = useRef<number | null>(null);

  // Per-empty-area wall-clock timing + switch count.
  const areaMsRef = useRef<Record<number, number>>({});
  const areaSinceRef = useRef(Date.now());
  const currentAreaRef = useRef<number>(
    useCoverageBetaStore.getState().activeEmptyAreaIdx,
  );
  const areaSwitchesRef = useRef(0);

  // Stable mirror of isIdle so DOM handlers see the latest without re-registering.
  const isIdleRef = useRef(isIdle);
  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  // The onboarding tutorial's open/closed state — a fourth pause source
  // alongside idle/hidden/sleep/blur, so a labeler reading a step doesn't
  // get docked active time, and `time_to_first_action` isn't measured
  // against wall-clock the tutorial itself consumed. Mirrored into a ref the
  // same way as isIdle just above, so DOM handlers see the latest value
  // without re-registering.
  const tutorialActive = useCoverageBetaStore((s) => s.tutorialActive);
  const tutorialActiveRef = useRef(tutorialActive);
  useEffect(() => {
    tutorialActiveRef.current = tutorialActive;
  }, [tutorialActive]);

  const shouldPauseNow = useCallback(
    () => isIdleRef.current || document.hidden || tutorialActiveRef.current,
    [],
  );

  const updatePauseState = useCallback((shouldPause: boolean) => {
    if (shouldPause && !isPausedRef.current) {
      isPausedRef.current = true;
      pauseStartRef.current = Date.now();
    } else if (!shouldPause && isPausedRef.current) {
      if (pauseStartRef.current !== null) {
        pauseIntervalsRef.current.push({
          start: pauseStartRef.current,
          end: Date.now(),
        });
      }
      isPausedRef.current = false;
      pauseStartRef.current = null;
    }
  }, []);

  // Merged pause intervals as of `now`, including the currently-open pause and
  // any graced window-blur period.
  const mergedPausesAt = useCallback((now: number): Interval[] => {
    const all: Interval[] = [...pauseIntervalsRef.current];
    if (isPausedRef.current && pauseStartRef.current !== null) {
      all.push({ start: pauseStartRef.current, end: now });
    }
    if (blurStartRef.current !== null) {
      const gracedStart = blurStartRef.current + WINDOW_BLUR_GRACE_MS;
      if (gracedStart < now) all.push({ start: gracedStart, end: now });
    }
    return mergeIntervals(all.sort((a, b) => a.start - b.start));
  }, []);

  const pausedBetween = (merged: Interval[], from: number, to: number) =>
    merged.reduce((sum, { start, end }) => {
      const s = Math.max(start, from);
      const e = Math.min(end, to);
      return sum + Math.max(0, e - s);
    }, 0);

  const activeAt = useCallback(
    (now: number) => {
      const merged = mergedPausesAt(now);
      return Math.max(
        0,
        now -
          taskStartRef.current -
          pausedBetween(merged, taskStartRef.current, now),
      );
    },
    [mergedPausesAt],
  );

  // Reset all tracking when a new task loads.
  useEffect(() => {
    taskStartRef.current = Date.now();
    pauseIntervalsRef.current = [];
    lastTickRef.current = Date.now();
    blurStartRef.current = null;
    firstActionActiveRef.current = null;
    areaMsRef.current = {};
    areaSinceRef.current = Date.now();
    currentAreaRef.current = useCoverageBetaStore.getState().activeEmptyAreaIdx;
    areaSwitchesRef.current = 0;
    const paused = shouldPauseNow();
    isPausedRef.current = paused;
    pauseStartRef.current = paused ? Date.now() : null;
  }, [taskId, shouldPauseNow]);

  // Attribute wall-clock time to the active empty area, and count switches.
  useEffect(() => {
    const onChange = (
      state: ReturnType<typeof useCoverageBetaStore.getState>,
    ) => {
      if (state.activeEmptyAreaIdx === currentAreaRef.current) return;
      const now = Date.now();
      const prev = currentAreaRef.current;
      areaMsRef.current[prev] =
        (areaMsRef.current[prev] ?? 0) + (now - areaSinceRef.current);
      currentAreaRef.current = state.activeEmptyAreaIdx;
      areaSinceRef.current = now;
      areaSwitchesRef.current += 1;
    };
    return useCoverageBetaStore.subscribe(onChange);
  }, [taskId]);

  // idle/tutorial → pause machine
  useEffect(() => {
    updatePauseState(shouldPauseNow());
  }, [isIdle, tutorialActive, updatePauseState, shouldPauseNow]);

  // tab visibility
  useEffect(() => {
    const handler = () => updatePauseState(shouldPauseNow());
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [updatePauseState, shouldPauseNow]);

  // sleep / JS-suspension gap detection
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTickRef.current;
      if (gap > SLEEP_GAP_MS && !isPausedRef.current) {
        const last =
          pauseIntervalsRef.current[pauseIntervalsRef.current.length - 1];
        const alreadyCovered = last != null && last.end >= lastTickRef.current;
        if (!alreadyCovered) {
          pauseIntervalsRef.current.push({
            start: lastTickRef.current,
            end: now,
          });
        }
      }
      lastTickRef.current = now;
    }, 1_000);
    return () => clearInterval(interval);
  }, []);

  // window blur/focus (alt-tab away)
  useEffect(() => {
    const handleBlur = () => {
      blurStartRef.current = Date.now();
    };
    const handleFocus = () => {
      if (blurStartRef.current !== null) {
        const start = blurStartRef.current + WINDOW_BLUR_GRACE_MS;
        const end = Date.now();
        if (start < end) pauseIntervalsRef.current.push({ start, end });
      }
      blurStartRef.current = null;
    };
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Record time-to-first-action the first time the store shows a labeling action.
  useEffect(() => {
    const check = (state: ReturnType<typeof useCoverageBetaStore.getState>) => {
      if (firstActionActiveRef.current !== null) return;
      if (hasLabelingAction(state)) {
        firstActionActiveRef.current = activeAt(Date.now());
      }
    };
    check(useCoverageBetaStore.getState());
    return useCoverageBetaStore.subscribe(check);
  }, [taskId, activeAt]);

  // Register the getter for the submit button to read at submit time.
  useEffect(() => {
    const getter = () => {
      const now = Date.now();
      const merged = mergedPausesAt(now);
      const total = Math.max(0, now - taskStartRef.current);
      const idle = pausedBetween(merged, taskStartRef.current, now);
      // Snapshot per-area time, adding the current area's in-progress span.
      const areaMs = { ...areaMsRef.current };
      areaMs[currentAreaRef.current] =
        (areaMs[currentAreaRef.current] ?? 0) + (now - areaSinceRef.current);
      return {
        total_duration: total,
        active_duration: Math.max(0, total - idle),
        idle_duration: idle,
        time_to_first_action: firstActionActiveRef.current,
        area_active_ms: areaMs,
        area_switches: areaSwitchesRef.current,
      };
    };
    timingGetter = getter;
    return () => {
      // Only clear if we're still the active getter; a newer instance may have
      // already replaced us during a task transition.
      if (timingGetter === getter) {
        timingGetter = null;
      }
    };
  }, [mergedPausesAt]);

  return null;
}
